import type { LucideIcon } from 'lucide-react';
import { Calendar, Check, Eye, EyeOff } from 'lucide-react';
import type { PillVariant } from '~/components/pill';

export interface StatusInput {
	isActive: boolean;
	activeFrom: Date | null;
	activeTo: Date | null;
	code: string | null;
	actionCount: number;
	isVisible: boolean;
	hideFromOffers: boolean;
	now?: Date;
}

export interface StatusPillSpec {
	variant: PillVariant;
	labelKey: string;
	icon?: LucideIcon;
}

export type PromotionState = 'active' | 'scheduled' | 'expired' | 'draft' | 'inactive';

/** The single state classification used by status pills, grid tabs, and tab counts. */
export function getPromotionState(input: StatusInput): PromotionState {
	const now = input.now ?? new Date();
	if (!input.code || input.actionCount === 0) return 'draft';
	if (!input.isActive) return 'inactive';
	if (input.activeTo && input.activeTo < now) return 'expired';
	if (input.activeFrom && input.activeFrom > now) return 'scheduled';
	return 'active';
}

const STATE_PILL: Record<PromotionState, StatusPillSpec> = {
	active: { variant: 'success', labelKey: 'promotion.status.active', icon: Check },
	scheduled: { variant: 'info', labelKey: 'promotion.status.scheduled', icon: Calendar },
	expired: { variant: 'danger', labelKey: 'promotion.status.expired' },
	draft: { variant: 'warning', labelKey: 'promotion.status.draft' },
	inactive: { variant: 'warning', labelKey: 'promotion.status.inactive' }
};

/**
 * Derives status pills from policy state. Shared by the editor head, the right
 * rail, and the grid so the mapping lives in one place (07-grid-spec.md status
 * mapping + 08-backend.md draft/scheduled definitions).
 *
 * Per the approved designs the active flag and the lifecycle are SEPARATE pills:
 * an in-force or scheduled or expired policy still reads `Ενεργή` while
 * `Προγραμματισμένη`/`Έληξε` overlays the lifecycle (e.g. grid row "Black Friday"
 * = Ενεργή + Έληξε). A draft collapses to the single `Πρόχειρη` pill.
 * Callers compose: active → visibility → lifecycle.
 */
export function computePromotionStatus(input: StatusInput): {
	activePills: StatusPillSpec[];
	lifecyclePills: StatusPillSpec[];
	visibilityPills: StatusPillSpec[];
} {
	const state = getPromotionState(input);

	const activePills: StatusPillSpec[] = [];
	const lifecyclePills: StatusPillSpec[] = [];
	if (state === 'draft') {
		activePills.push(STATE_PILL.draft);
	} else {
		activePills.push(input.isActive ? STATE_PILL.active : STATE_PILL.inactive);
		if (state === 'expired') lifecyclePills.push(STATE_PILL.expired);
		else if (state === 'scheduled') lifecyclePills.push(STATE_PILL.scheduled);
	}

	const visibilityPills: StatusPillSpec[] = [];
	if (input.hideFromOffers) {
		visibilityPills.push({ variant: 'neutral', labelKey: 'promotion.status.hidden', icon: EyeOff });
	} else if (input.isVisible) {
		visibilityPills.push({ variant: 'primary', labelKey: 'promotion.status.visible', icon: Eye });
	}

	return { activePills, lifecyclePills, visibilityPills };
}

/**
 * Relative-time descriptor for a policy's validity window, used by the rail
 * (e.g. "σε 8 ημέρες") and reusable wherever a one-line relative is needed.
 * Returns an i18n key + interpolation count; `null` when there are no dates.
 */
export function getValidityRelative(input: Pick<StatusInput, 'activeFrom' | 'activeTo' | 'now'>): {
	key: string;
	count: number;
} | null {
	const now = input.now ?? new Date();
	if (!input.activeFrom && !input.activeTo) return null;
	if (input.activeTo && input.activeTo < now) {
		return { key: 'promotion.rail.relativeAgoDays', count: daysBetween(now, input.activeTo) };
	}
	if (input.activeFrom && input.activeFrom > now) {
		return { key: 'promotion.rail.relativeInDays', count: daysBetween(input.activeFrom, now) };
	}
	return { key: 'promotion.rail.relativeInForce', count: 0 };
}

function daysBetween(a: Date, b: Date): number {
	return Math.abs(Math.round((a.getTime() - b.getTime()) / 86_400_000));
}
